import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, Clock, X, Check } from "lucide-react";
import { http } from "@/lib/api";

const STATUS_LABEL = {
  pending_ops: { label: "Awaiting Ops", cls: "bg-amber-50 text-amber-800 border-amber-200" },
  pending_founder: { label: "Awaiting Founder", cls: "bg-blue-50 text-blue-800 border-blue-200" },
  approved: { label: "Approved", cls: "bg-emerald-50 text-emerald-800 border-emerald-200" },
  rejected: { label: "Rejected", cls: "bg-rose-50 text-rose-800 border-rose-200" },
};

export default function ApprovalsPanel({ tier }) {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  const canOpsApprove = tier === "ops" || tier === "founder";
  const canFounderApprove = tier === "founder";

  const fetchAll = async () => {
    try {
      const { data } = await http.get("/approval-requests");
      setRequests(data.requests || []);
    } catch (e) {
      console.error("Failed to load approval requests:", e);
    }
    finally { setLoading(false); }
  };

  useEffect(() => {
    fetchAll();
    const t = setInterval(fetchAll, 15000);
    return () => clearInterval(t);
  }, []);

  const act = async (id, path, body = null) => {
    try {
      await http.post(`/approval-requests/${id}/${path}`, body || {});
      toast.success("Updated");
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Action failed");
    }
  };

  const pending = requests.filter((r) => r.status === "pending_ops" || r.status === "pending_founder");

  return (
    <section data-testid="approvals-panel" className="mt-6 bg-white rounded-2xl border border-slate-100 p-5 shadow-sm">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <ShieldCheck className="w-4 h-4 text-[#0B1E3F]" />
          <h3 className="font-serif text-lg">Reassignment approvals</h3>
          {pending.length > 0 && (
            <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 text-amber-800 px-2 py-0.5 text-[10px] font-semibold">
              <Clock className="w-3 h-3" /> {pending.length} pending
            </span>
          )}
        </div>
      </div>

      {loading && <div className="text-sm text-slate-400">Loading…</div>}
      {!loading && requests.length === 0 && (
        <div className="text-sm text-slate-400">No reassignment requests yet.</div>
      )}

      <div className="space-y-3">
        {requests.map((r) => {
          const badge = STATUS_LABEL[r.status] || { label: r.status, cls: "bg-slate-100 text-slate-700 border-slate-200" };
          return (
            <div
              key={r.id}
              data-testid={`approval-row-${r.id}`}
              className="border border-slate-100 rounded-xl px-4 py-3 flex flex-wrap items-start gap-3 bg-slate-50/40"
            >
              <div className="flex-1 min-w-[220px]">
                <div className="text-sm font-semibold">{r.lead_name}</div>
                <div className="text-xs text-slate-500 mt-0.5">
                  {r.from_advisor || "Unassigned"} → <span className="font-semibold text-slate-700">{r.to_advisor || "Unassigned"}</span>
                </div>
                <div className="text-[11px] text-slate-500 mt-1">
                  by {r.requested_by_name} · {new Date(r.created_at).toLocaleString()}
                </div>
                {r.reason && (
                  <div className="text-xs text-slate-600 mt-2 italic">"{r.reason}"</div>
                )}
                {r.reject_reason && (
                  <div className="text-xs text-rose-600 mt-1">Rejected: {r.reject_reason}</div>
                )}
              </div>
              <div className="flex items-center gap-2">
                <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${badge.cls}`}>
                  {badge.label}
                </span>
                {r.status === "pending_ops" && canOpsApprove && (
                  <>
                    <button
                      data-testid={`approve-ops-${r.id}`}
                      onClick={() => act(r.id, "ops-approve")}
                      className="inline-flex items-center gap-1 rounded-lg bg-[#0B1E3F] text-white px-3 py-1.5 text-xs font-semibold hover:bg-[#162A4D]"
                    >
                      <Check className="w-3 h-3" /> Ops approve
                    </button>
                    <button
                      data-testid={`reject-${r.id}`}
                      onClick={() => {
                        const reason = prompt("Reason for rejection? (optional)") ?? "";
                        act(r.id, "reject", { reason });
                      }}
                      className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white text-slate-600 px-3 py-1.5 text-xs font-semibold hover:bg-slate-50"
                    >
                      <X className="w-3 h-3" /> Reject
                    </button>
                  </>
                )}
                {r.status === "pending_founder" && canFounderApprove && (
                  <>
                    <button
                      data-testid={`approve-founder-${r.id}`}
                      onClick={() => act(r.id, "founder-approve")}
                      className="inline-flex items-center gap-1 rounded-lg bg-[#C9A961] text-[#0B1E3F] px-3 py-1.5 text-xs font-semibold hover:bg-[#D7B974]"
                    >
                      <Check className="w-3 h-3" /> Founder approve
                    </button>
                    <button
                      data-testid={`reject-${r.id}`}
                      onClick={() => {
                        const reason = prompt("Reason for rejection? (optional)") ?? "";
                        act(r.id, "reject", { reason });
                      }}
                      className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white text-slate-600 px-3 py-1.5 text-xs font-semibold hover:bg-slate-50"
                    >
                      <X className="w-3 h-3" /> Reject
                    </button>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
